import { useState, useRef, useCallback } from "react";

// ============================================================================
//  EscanerCuestionario — Escaneo por lotes con cola en paralelo.
//
//  Flujo:
//   1. El operador escanea las páginas de una persona (las que sean).
//   2. Aprieta "Listo": el formulario se manda al back (leer-lote) y entra a
//      la COLA como "procesando".
//   3. SIN esperar la respuesta, ya puede escanear el siguiente formulario.
//   4. La cola muestra cada envío con su estado: procesando / listo / error.
//
//  Genérico (no menciona ningún cuestionario puntual) y sin botón de enviar
//  (el envío a FormAssembly es automático luego, en la validación).
//
//  Props:
//    baseApi     : base de la API (ej. "http://localhost:5132")
//    plantillaId : id/nº de formulario que se está escaneando
//    titulo      : opcional (default "Escanear formulario")
// ============================================================================

const TEAL = "#286076";
const TEAL_BRILLANTE = "#19A0AF";
const TINTA = "#1a2b31";


let _seq = 0;
const nuevoId = () => `f${Date.now()}_${_seq++}`;

export default function EscanerCuestionario({
  baseApi = "http://0.0.0.0:5132",
  plantillaId = "",
  titulo = "Escanear formulario",
}) {
  const [paginasActuales, setPaginasActuales] = useState([]); // [{ url, file }]
  const [cola, setCola] = useState([]); // { id, cantPaginas, estado, mensaje }
  const inputRef = useRef(null);

  const onCapturar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPaginasActuales((prev) => [...prev, { url, file }]);
    e.target.value = "";
  };

  const quitarPagina = (idx) =>
    setPaginasActuales((prev) => prev.filter((_, i) => i !== idx));

  const actualizarEnCola = (id, cambios) =>
    setCola((prev) => prev.map((f) => (f.id === id ? { ...f, ...cambios } : f)));

  const quitarDeCola = (id) => setCola((prev) => prev.filter((f) => f.id !== id));

  const terminarFormulario = useCallback(() => {
    if (paginasActuales.length === 0) return;

    const idForm = nuevoId();
    const paginas = paginasActuales;

    setCola((prev) => [
      { id: idForm, cantPaginas: paginas.length, estado: "procesando", mensaje: "" },
      ...prev,
    ]);
    setPaginasActuales([]); // limpiar YA, sin esperar la respuesta

    (async () => {
      try {
        const fd = new FormData();
        paginas.forEach((p) => fd.append("imagenes", p.file));
        fd.append("paginaIndices", paginas.map((_, i) => i).join(","));
        fd.append("plantillaId", plantillaId);

        const res = await fetch(`${baseApi}/api/cuestionario/leer-lote`, {
          method: "POST",
          body: fd,
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error(data?.error || `Error ${res.status}`);

        actualizarEnCola(idForm, {
          estado: "listo",
          mensaje: `${data?.totalCampos ?? "?"} campos leídos`,
        });
      } catch (err) {
        actualizarEnCola(idForm, { estado: "error", mensaje: err.message || "Falló el envío" });
      } finally {
        paginas.forEach((p) => URL.revokeObjectURL(p.url));
      }
    })();
  }, [paginasActuales, baseApi, plantillaId]);

  const procesando = cola.filter((f) => f.estado === "procesando").length;
  const listos = cola.filter((f) => f.estado === "listo").length;

  return (
    <div style={S.pagina}>
      <div style={S.tope}>
        <div>
          <div style={S.marca}>Escáner</div>
          <h1 style={S.titulo}>{titulo}</h1>
          {plantillaId ? <div style={S.formId}>Formulario N° {plantillaId}</div> : null}
        </div>
        {(procesando > 0 || listos > 0) && (
          <div style={S.badges}>
            {procesando > 0 && <span style={S.badgeProc}>{procesando} procesando</span>}
            {listos > 0 && <span style={S.badgeOk}>{listos} listos</span>}
          </div>
        )}
      </div>

      <div style={S.zonaActual}>
        <div style={S.zonaActualHead}>
          <span style={S.zonaActualTit}>
            {paginasActuales.length === 0
              ? "Escaneá la primera página"
              : `${paginasActuales.length} ${paginasActuales.length === 1 ? "página" : "páginas"} de esta persona`}
          </span>
        </div>

        <div style={S.miniaturas}>
          {paginasActuales.map((p, i) => (
            <div key={i} style={S.miniWrap}>
              <img src={p.url} alt={`Página ${i + 1}`} style={S.mini} />
              <button style={S.miniQuitar} onClick={() => quitarPagina(i)} aria-label="Quitar">×</button>
              <span style={S.miniNum}>{i + 1}</span>
            </div>
          ))}
          <button style={S.btnAgregar} onClick={() => inputRef.current?.click()}>
            <span style={S.btnAgregarMas}>+</span>
            <span style={S.btnAgregarTxt}>
              {paginasActuales.length === 0 ? "Escanear" : "Otra página"}
            </span>
          </button>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onCapturar}
          style={{ display: "none" }}
        />
      </div>

      <button
        style={{ ...S.btnListo, opacity: paginasActuales.length === 0 ? 0.4 : 1 }}
        disabled={paginasActuales.length === 0}
        onClick={terminarFormulario}
      >
        Listo · terminar formulario
      </button>
      <p style={S.hint}>
        Al terminar, este formulario se procesa solo y podés seguir con el siguiente.
      </p>

      {cola.length > 0 && (
        <div style={S.cola}>
          <div style={S.colaTit}>Formularios enviados</div>
          {cola.map((f, i) => (
            <div key={f.id} style={S.item}>
              <div style={S.itemIzq}>
                <EstadoPunto estado={f.estado} />
                <div>
                  <div style={S.itemTit}>Formulario #{cola.length - i}</div>
                  <div style={S.itemSub}>
                    {f.cantPaginas} {f.cantPaginas === 1 ? "página" : "páginas"}
                    {f.mensaje ? ` · ${f.mensaje}` : ""}
                  </div>
                </div>
              </div>
              <div style={S.itemDer}>
                {f.estado === "procesando" && <Spinner />}
                {f.estado === "listo" && <span style={S.check}>✓</span>}
                {f.estado === "error" && (
                  <button style={S.btnQuitar} onClick={() => quitarDeCola(f.id)}>Quitar</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EstadoPunto({ estado }) {
  const color =
    estado === "listo" ? "#1D9E75" : estado === "error" ? "#C0492E" : TEAL_BRILLANTE;
  return <span style={{ ...S.punto, background: color }} />;
}

function Spinner() {
  return (
    <span style={S.spinner}>
      <svg width="18" height="18" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9" fill="none" stroke="#d3d8d4" strokeWidth="3" />
        <path d="M12 3a9 9 0 0 1 9 9" fill="none" stroke={TEAL} strokeWidth="3" strokeLinecap="round" />
      </svg>
    </span>
  );
}

const S = {
  pagina: {
    minHeight: "100vh", background: "#E7E9E6", padding: "18px 16px 40px",
    fontFamily: "-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif", color: TINTA,
    maxWidth: 480, margin: "0 auto", boxSizing: "border-box",
  },
  tope: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 },
  marca: { fontSize: 11, fontWeight: 700, color: TEAL, letterSpacing: ".05em", textTransform: "uppercase" },
  titulo: { fontSize: 22, fontWeight: 700, margin: "3px 0 2px", letterSpacing: "-.02em" },
  formId: { fontSize: 13, color: "#6b706e", fontVariantNumeric: "tabular-nums" },
  badges: { display: "flex", flexDirection: "column", gap: 5, alignItems: "flex-end" },
  badgeProc: { fontSize: 11, fontWeight: 700, color: "#0a6b78", background: "#E1F0F2", padding: "3px 9px", borderRadius: 20 },
  badgeOk: { fontSize: 11, fontWeight: 700, color: "#0F6E56", background: "#E1F5EE", padding: "3px 9px", borderRadius: 20 },
  zonaActual: { background: "#fff", borderRadius: 16, padding: 16, boxShadow: "0 2px 12px rgba(0,0,0,.06)" },
  zonaActualHead: { marginBottom: 12 },
  zonaActualTit: { fontSize: 14, fontWeight: 600, color: "#4a4f4d" },
  miniaturas: { display: "flex", flexWrap: "wrap", gap: 10 },
  miniWrap: { position: "relative", width: 84, height: 108, borderRadius: 10, overflow: "hidden", border: "1px solid #e2e6e3" },
  mini: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  miniQuitar: {
    position: "absolute", top: 3, right: 3, width: 20, height: 20, borderRadius: "50%",
    border: "none", background: "rgba(0,0,0,.55)", color: "#fff", fontSize: 15, lineHeight: "20px",
    cursor: "pointer", padding: 0,
  },
  miniNum: {
    position: "absolute", bottom: 3, left: 3, fontSize: 11, fontWeight: 700, color: "#fff",
    background: "rgba(0,0,0,.55)", borderRadius: 5, padding: "1px 6px",
  },
  btnAgregar: {
    width: 84, height: 108, borderRadius: 10, border: `2px dashed ${TEAL}`, background: "#F4F8F9",
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    gap: 4, cursor: "pointer", color: TEAL,
  },
  btnAgregarMas: { fontSize: 26, fontWeight: 300, lineHeight: 1 },
  btnAgregarTxt: { fontSize: 12, fontWeight: 600 },
  btnListo: {
    width: "100%", marginTop: 16, background: TEAL, color: "#fff", border: "none",
    borderRadius: 14, padding: "16px", fontSize: 16, fontWeight: 700, cursor: "pointer",
  },
  hint: { fontSize: 12, color: "#8a8f8d", textAlign: "center", margin: "8px 0 0", lineHeight: 1.4 },
  cola: { marginTop: 26 },
  colaTit: { fontSize: 12, fontWeight: 700, color: "#8a8f8d", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 10 },
  item: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    background: "#fff", borderRadius: 12, padding: "12px 14px", marginBottom: 8,
    boxShadow: "0 1px 6px rgba(0,0,0,.05)",
  },
  itemIzq: { display: "flex", alignItems: "center", gap: 11 },
  punto: { width: 9, height: 9, borderRadius: "50%", flexShrink: 0 },
  itemTit: { fontSize: 14, fontWeight: 600 },
  itemSub: { fontSize: 12, color: "#8a8f8d", marginTop: 1 },
  itemDer: { display: "flex", alignItems: "center" },
  check: { color: "#1D9E75", fontSize: 18, fontWeight: 700 },
  spinner: { display: "inline-flex", animation: "girar 1s linear infinite" },
  btnQuitar: { background: "none", border: "none", color: "#C0492E", fontSize: 13, fontWeight: 600, cursor: "pointer" },
};

if (typeof document !== "undefined" && !document.getElementById("escaner-spin")) {
  const st = document.createElement("style");
  st.id = "escaner-spin";
  st.textContent = "@keyframes girar{to{transform:rotate(360deg)}}";
  document.head.appendChild(st);
}